import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { niciApi } from '../../services/api'
import { NiciOfferta, NiciOffertaRiga } from '../../types/nici'
import toast from 'react-hot-toast'
import { Plus, Trash2, FileText, FileSpreadsheet } from 'lucide-react'

function scaricaBlob(data: Blob, filename: string) {
  const url = URL.createObjectURL(data)
  const a = document.createElement('a')
  a.href = url; a.download = filename; a.click()
  URL.revokeObjectURL(url)
}

function PannelloRiconciliazione({ righe }: { righe: NiciOffertaRiga[] }) {
  const righeAuto = righe.filter(r => r.origine === 'AUTO_VERBALE')
  const scostamentiNonMotivati = righeAuto.filter(
    r => r.quantita_da_verbale !== undefined && r.quantita !== r.quantita_da_verbale && !r.motivo_scostamento,
  )
  const fedele = scostamentiNonMotivati.length === 0

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm">Riconciliazione verbale ↔ offerta</h3>
        <span className="badge text-xs px-2 py-0.5 rounded-full"
          style={{ background: fedele ? 'rgba(34,197,94,.12)' : 'rgba(220,38,38,.12)', color: fedele ? '#15803d' : '#dc2626' }}>
          {fedele ? 'Fedele al sopralluogo' : `${scostamentiNonMotivati.length} scostamenti senza motivo`}
        </span>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-slate-500 border-b border-slate-100">
            <th className="p-1.5">Voce</th><th className="p-1.5 text-right">Q.tà verbale</th>
            <th className="p-1.5 text-right">Q.tà offerta</th><th className="p-1.5">Motivo</th>
          </tr>
        </thead>
        <tbody>
          {righeAuto.map(r => (
            <tr key={r.id} className="border-b border-slate-50">
              <td className="p-1.5">{r.descrizione}</td>
              <td className="p-1.5 text-right">{r.quantita_da_verbale ?? '—'}</td>
              <td className="p-1.5 text-right">{r.quantita}</td>
              <td className="p-1.5 text-slate-500">{r.motivo_scostamento || (r.quantita !== r.quantita_da_verbale ? '⚠ mancante' : '—')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function NiciOffertaDetail() {
  const { id } = useParams<{ id: string }>()
  const qc = useQueryClient()
  const [nuovaRiga, setNuovaRiga] = useState(false)
  const [bozzaRiga, setBozzaRiga] = useState({ descrizione: '', quantita: '1', um: 'N°', prezzo_unitario_ricavo: '0' })

  const { data: offerta, isLoading } = useQuery({
    queryKey: ['nici-offerta', id],
    queryFn: () => niciApi.offerte.get(id!).then(r => r.data as NiciOfferta),
    enabled: !!id,
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['nici-offerta', id] })

  const creaRigaMut = useMutation({
    mutationFn: () => niciApi.offerte.righe.create(id!, {
      descrizione: bozzaRiga.descrizione, quantita: parseFloat(bozzaRiga.quantita),
      um: bozzaRiga.um, prezzo_unitario_ricavo: parseFloat(bozzaRiga.prezzo_unitario_ricavo),
    }),
    onSuccess: () => { invalidate(); setNuovaRiga(false); toast.success('Riga aggiunta') },
    onError: () => toast.error('Errore aggiunta riga'),
  })
  const eliminaRigaMut = useMutation({
    mutationFn: (rigaId: string) => niciApi.offerte.righe.delete(id!, rigaId),
    onSuccess: () => { invalidate(); toast.success('Riga eliminata') },
    onError: () => toast.error('Errore eliminazione riga'),
  })
  const patchRigaMotivoMut = useMutation({
    mutationFn: (v: { rigaId: string; motivo_scostamento: string }) =>
      niciApi.offerte.righe.update(id!, v.rigaId, { motivo_scostamento: v.motivo_scostamento }),
    onSuccess: () => invalidate(),
    onError: () => toast.error('Errore aggiornamento motivo'),
  })

  const azioneMut = useMutation({
    mutationFn: (azione: 'submit' | 'approva' | 'rifiuta' | 'invia' | 'nuova-revisione') => {
      switch (azione) {
        case 'submit': return niciApi.offerte.submit(id!)
        case 'approva': return niciApi.offerte.approva(id!)
        case 'rifiuta': return niciApi.offerte.rifiuta(id!)
        case 'invia': return niciApi.offerte.invia(id!)
        case 'nuova-revisione': return niciApi.offerte.nuovaRevisione(id!)
      }
    },
    onSuccess: () => { invalidate(); toast.success('Operazione completata') },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      toast.error(typeof detail === 'string' ? detail : 'Operazione non riuscita')
    },
  })
  const esitoMut = useMutation({
    mutationFn: (esito: 'ACCETTATA' | 'RIFIUTATA') => niciApi.offerte.esito(id!, esito),
    onSuccess: () => { invalidate(); toast.success('Esito registrato') },
    onError: () => toast.error('Errore registrazione esito'),
  })

  if (isLoading || !offerta) return <div className="card p-8 text-center text-sm text-slate-400">Caricamento...</div>

  const righe = offerta.righe ?? []
  const includiMargini = offerta.margine_pct !== undefined
  const editabile = offerta.stato === 'BOZZA'

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="page-title">{offerta.codice} <span className="text-sm text-slate-400">v{offerta.revisione}</span></h1>
          <p className="page-subtitle">Stato: {offerta.stato}</p>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary flex items-center gap-2 text-sm" onClick={async () => {
            const resp = await niciApi.offerte.pdf(offerta.id)
            scaricaBlob(resp.data as Blob, `${offerta.codice}.pdf`)
          }}><FileText size={14} /> PDF</button>
          {includiMargini && (
            <button className="btn-secondary flex items-center gap-2 text-sm" onClick={async () => {
              const resp = await niciApi.offerte.computoXlsx(offerta.id)
              scaricaBlob(resp.data as Blob, `computo_${offerta.codice}.xlsx`)
            }}><FileSpreadsheet size={14} /> Excel</button>
          )}
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {offerta.stato === 'BOZZA' && (
          <button className="btn-primary text-sm" onClick={() => azioneMut.mutate('submit')}>Invia in approvazione</button>
        )}
        {offerta.stato === 'DA_APPROVARE' && (
          <>
            <button className="btn-primary text-sm" onClick={() => azioneMut.mutate('approva')}>Approva</button>
            <button className="btn-secondary text-sm" onClick={() => azioneMut.mutate('rifiuta')}>Rifiuta</button>
          </>
        )}
        {offerta.stato === 'APPROVATA' && (
          <button className="btn-primary text-sm" onClick={() => azioneMut.mutate('invia')}>Invia al cliente</button>
        )}
        {offerta.stato === 'INVIATA' && (
          <>
            <button className="btn-primary text-sm" onClick={() => esitoMut.mutate('ACCETTATA')}>Cliente ha accettato</button>
            <button className="btn-secondary text-sm" onClick={() => esitoMut.mutate('RIFIUTATA')}>Cliente ha rifiutato</button>
          </>
        )}
        {offerta.stato === 'RIFIUTATA' && (
          <button className="btn-primary text-sm" onClick={() => azioneMut.mutate('nuova-revisione')}>Crea nuova revisione</button>
        )}
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
              <th className="p-2">Descrizione</th><th className="p-2">NMU</th><th className="p-2">UM</th>
              <th className="p-2 text-right">Q.tà</th><th className="p-2 text-right">Prezzo</th>
              <th className="p-2 text-right">Totale</th>
              {includiMargini && <th className="p-2 text-right">Margine</th>}
              {editabile && <th className="p-2 w-10"></th>}
            </tr>
          </thead>
          <tbody>
            {righe.map(r => (
              <tr key={r.id} className="border-b border-slate-50">
                <td className="p-2">{r.descrizione}</td>
                <td className="p-2 font-mono text-xs">{r.nmu_code || '—'}</td>
                <td className="p-2 text-xs">{r.um}</td>
                <td className="p-2 text-right">{r.quantita}</td>
                <td className="p-2 text-right">€ {r.prezzo_unitario_ricavo.toFixed(2)}</td>
                <td className="p-2 text-right">€ {r.totale_ricavo.toFixed(2)}</td>
                {includiMargini && <td className="p-2 text-right">€ {(r.margine ?? 0).toFixed(2)}</td>}
                {editabile && (
                  <td className="p-2">
                    <button className="btn-ghost p-1" onClick={() => eliminaRigaMut.mutate(r.id)}>
                      <Trash2 size={13} style={{ color: '#dc2626' }} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="font-semibold border-t border-slate-200">
              <td className="p-2" colSpan={5}>Totale</td>
              <td className="p-2 text-right">€ {offerta.totale_ricavo.toFixed(2)}</td>
              {includiMargini && <td className="p-2 text-right">€ {(offerta.margine ?? 0).toFixed(2)} ({(offerta.margine_pct ?? 0).toFixed(1)}%)</td>}
              {editabile && <td />}
            </tr>
          </tfoot>
        </table>

        {editabile && (
          <div className="p-3 border-t border-slate-100">
            {!nuovaRiga ? (
              <button className="btn-secondary flex items-center gap-2 text-sm" onClick={() => setNuovaRiga(true)}>
                <Plus size={14} /> Aggiungi riga manuale
              </button>
            ) : (
              <div className="flex gap-2 items-end flex-wrap">
                <input className="input text-sm flex-1 min-w-[200px]" placeholder="Descrizione"
                  value={bozzaRiga.descrizione} onChange={e => setBozzaRiga(p => ({ ...p, descrizione: e.target.value }))} />
                <input className="input text-sm w-20" placeholder="Q.tà" value={bozzaRiga.quantita}
                  onChange={e => setBozzaRiga(p => ({ ...p, quantita: e.target.value }))} />
                <input className="input text-sm w-24" placeholder="Prezzo" value={bozzaRiga.prezzo_unitario_ricavo}
                  onChange={e => setBozzaRiga(p => ({ ...p, prezzo_unitario_ricavo: e.target.value }))} />
                <button className="btn-primary text-sm" onClick={() => {
                  if (!bozzaRiga.descrizione.trim()) { toast.error('Descrizione obbligatoria'); return }
                  creaRigaMut.mutate()
                }}>Aggiungi</button>
                <button className="btn-ghost text-sm" onClick={() => setNuovaRiga(false)}>Annulla</button>
              </div>
            )}
          </div>
        )}
      </div>

      {righe.some(r => r.origine === 'AUTO_VERBALE') && <PannelloRiconciliazione righe={righe} />}

      {editabile && righe.some(r => r.origine === 'AUTO_VERBALE' && r.quantita !== r.quantita_da_verbale && !r.motivo_scostamento) && (
        <div className="card p-4 space-y-2">
          <p className="text-sm text-slate-600">Motiva gli scostamenti prima di inviare in approvazione:</p>
          {righe.filter(r => r.origine === 'AUTO_VERBALE' && r.quantita !== r.quantita_da_verbale && !r.motivo_scostamento).map(r => (
            <div key={r.id} className="flex gap-2 items-center">
              <span className="text-xs text-slate-500 w-48 truncate">{r.descrizione}</span>
              <input className="input text-sm flex-1" placeholder="Motivo scostamento"
                onBlur={e => { if (e.target.value.trim()) patchRigaMotivoMut.mutate({ rigaId: r.id, motivo_scostamento: e.target.value }) }} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
